// eslint-disable-next-line no-unused-vars
import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import When from 'components/When';

import { ICONS } from 'constants/icons';
import appMessages from 'features/App/App.messages';
import { bem } from 'helpers/class-name';
import { FlyoutContentContext, FlyoutContext, FlyoutFooterContext, FlyoutTitleContext } from './FlyoutContext';

import './Flyout.scss';

function isTargetTheDropdownOverlay({ target }) {
  const hasDropdownOverlayClass = target.classList?.contains('dropdown2__overlay');

  return hasDropdownOverlayClass;
}

function isTargetADropdownItem({ target }) {
  const hasDropdownItemClass =
    target.classList?.contains('dropdown-item2') || target.classList?.contains('dropdown-menu-item');
  const hasRoleOption = target.role === 'option';

  return hasDropdownItemClass && hasRoleOption;
}

function isClickWithinElement(ref, event) {
  if (ref.current) {
    const node = ref.current instanceof Node ? ref.current : ref.current.node;
    const isFromElement = event.target === node;
    if (isFromElement) {
      return true;
    }

    const isFromChild = node.contains?.(event.target);
    if (isFromChild) {
      return true;
    }

    const rect = node.getBoundingClientRect();
    const isWithinClientRect =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    return isWithinClientRect;
  }

  return false;
}

const { block, element } = bem('flyout');

const Flyout = ({ id, children, className, onClose, showBackButton, overrideOutsideClick }) => {
  const flyoutContainerRef = useRef(null);
  const flyoutTitleRef = useRef(null);
  const flyoutContentRef = useRef(null);
  const flyoutFooterRef = useRef(null);

  const [isFlyoutEntered, setIsFlyoutEntered] = useState(false);
  const { openFlyoutId, closeFlyout } = useContext(FlyoutContext);
  const isOpen = !!id && openFlyoutId === id;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      closeFlyout();
    }
  };

  const handleEnter = () => {
    setIsFlyoutEntered(true);
  };

  const handleExited = () => {
    setIsFlyoutEntered(false);
  };

  const handleCloseFlyoutOnOutsideClick = event => {
    if (!isOpen) {
      return;
    }

    const isFromDropdownOverlay = isTargetTheDropdownOverlay(event);
    if (isFromDropdownOverlay) {
      return;
    }

    const isFromDropdownItem = isTargetADropdownItem(event);
    if (isFromDropdownItem) {
      return;
    }

    const isWithinFlyout = isClickWithinElement(flyoutContainerRef, event);
    if (isWithinFlyout) {
      return;
    }

    if (overrideOutsideClick?.(event)) {
      return;
    }

    handleClose();
  };

  useEffect(() => {
    document.addEventListener('click', handleCloseFlyoutOnOutsideClick);

    return () => {
      document.removeEventListener('click', handleCloseFlyoutOnOutsideClick);
    };
  }, [isOpen]);

  const [isFlyoutBelowDecagon, setIsFlyoutBelowDecagon] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const handleDecagonEvent = event => {
      if (!isMounted) return;

      if (event.eventName === 'DUET_INITIALIZE') {
        setIsFlyoutBelowDecagon(true);
      }
      if (event.eventName === 'DUET_DEINITIALIZE') {
        setIsFlyoutBelowDecagon(false);
      }
    };

    if (window.duet) {
      window.duet.setEventListener(handleDecagonEvent);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const intl = useIntl();
  const closeAriaLabel = intl.formatMessage(appMessages.close);

  return createPortal(
    <>
      <CSSTransition
        className={block(className, { belowDecagon: isFlyoutBelowDecagon })}
        classNames={block()}
        in={isOpen}
        onEnter={handleEnter}
        onExited={handleExited}
        timeout={300}
        unmountOnExit>
        <div ref={flyoutContainerRef}>
          <header className={element('header', { hasBackButton: showBackButton })}>
            <When predicate={showBackButton}>
              <button className={element('close-button')} type="button" onClick={handleClose}>
                <Isvg src={ICONS.leftArrowSquare} aria-label={closeAriaLabel} />
              </button>
            </When>
            <h2 className={element('title')} ref={flyoutTitleRef} />
            <When predicate={!showBackButton}>
              <div className={element('button-wrapper')}>
                <button className={element('close-button', 'cross')} type="button" onClick={handleClose}>
                  <Isvg src={ICONS.cross} aria-label={closeAriaLabel} />
                </button>
              </div>
            </When>
          </header>
          <div className={element('content')} ref={flyoutContentRef} />
          <div className={element('footer')} ref={flyoutFooterRef} />
        </div>
      </CSSTransition>
      {isFlyoutEntered && (
        <FlyoutTitleContext.Provider value={flyoutTitleRef}>
          <FlyoutContentContext.Provider value={flyoutContentRef}>
            <FlyoutFooterContext.Provider value={flyoutFooterRef}>{children}</FlyoutFooterContext.Provider>
          </FlyoutContentContext.Provider>
        </FlyoutTitleContext.Provider>
      )}
    </>,
    document.body
  );
};

Flyout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onClose: PropTypes.func,
  overrideOutsideClick: PropTypes.func,
  showBackButton: PropTypes.bool,
};

export default Flyout;
